import React, { Fragment } from 'react';
import { connect } from 'redux-zero/react';
import { Collapse } from 'react-collapse';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';

import FaqPageService from './faq.service.js';
import Seo from '../../infrastructure/seo.js';
import { actions } from '../../shared/store.js';
import NotImplemented from '../../components/not-implemented/page-not-implemented.js';

import './faq.scss';

const scrollToTop = () => window.scrollTo({
	top: 0,
	behavior: 'smooth',
});

const FaqItem = ({
	data, index, isOpen, handleClick,
}) => (
	<div className="item">
		{data.value.question
		&& <h2 className={`${classNames({ open: isOpen })} question`} onClick={() => handleClick(index)}>{data.value.question}</h2>
		}
		{data.value.answer
		&& (
			<Collapse className="collapse-wrapper" isOpened={isOpen}>
				<div className="answer-wrapper">
					<div className="answer" dangerouslySetInnerHTML={{ __html: data.value.answer }} />
				</div>
			</Collapse>
		)
		}
	</div>
);

class FaqPage extends React.Component {
	static prefetchData = async language => FaqPageService.getFaqData(language);

	state = {
		openItem: null,
		hasError: false,
	};

	componentDidMount() {
		scrollToTop();
		if (this.props.faq.length === 0) {
			this.fetchData();
		} else {
			this.props.setLoading(false);
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.language.value !== this.props.language.value) {
			this.fetchData();
		}
	}

	fetchData() {
		this.props.setLoading(true);
		FaqPage.prefetchData(this.props.language.value)
			.then((res) => {
				this.props.setFaq(res);
				this.props.setLoading(false);
			})
			.catch((error) => {
				this.setState({ hasError: true });
				this.props.setLoading(false);
				console.log(error);
			});
	}

	handleClick = (i) => {
		if (this.state.openItem !== i) {
			this.setState({
				openItem: i,
			});
		} else {
			this.setState({
				openItem: null,
			});
		}
	};

	render() {
		const { faq, intl: { formatMessage }, addresses, phones, generalData } = this.props;
		const { openItem, hasError } = this.state;

		if (hasError || !faq) {
			return <NotImplemented title="FAQ" />;
		}

		return (
			<div className="faq-page">
				<Seo
					title={faq.title || formatMessage({ id: 'navigation.faq' })}
					description={faq.title || formatMessage({ id: 'navigation.faq' })}
					microdata={`{
									"@context": "https://schema.org",
									"@graph": [
										{
											"@type": "FAQPage",
											"name": "${faq.title || formatMessage({ id: 'navigation.faq' })}",
											"mainEntity": [
												${faq.groups && faq.groups.length > 0 && faq.groups.map(group => group.value.group.length > 0 && group.value.group.map(item => `
													{
														"@type": "Question",
														"name": "${item.value.question}",
														"acceptedAnswer": {
															"@type": "Answer",
															"text": "${item.value.answer.replace(/"/g, '\\"')}"
														}
													}
												`))}
											]
										},
										{
											"@type": "WebApplication",
											"name": "Inocrowd",
											"url": "${process.env.REACT_APP_WEBSITE_ENDPOINT}",
											"applicationCategory": "BusinessApplication",
											"operatingSystem": "Windows, Linux, OSX, iOS, Android",
											"sameAs": [
												"${process.env.REACT_APP_PLATFORM_ENDPOINT}",
												"https://www.facebook.com/InoCrowd/",
												"https://www.linkedin.com/company/inocrowd",
												"https://twitter.com/InoCrowd",
												"https://www.instagram.com/inocrowd",
												"https://www.youtube.com/channel/UC8JotJA9Txz_6NQXWuEwdvQ"
											],
											"provider": {
												"@type": "Organization",
												"name": "Inocrowd",
												${addresses.length > 0 && `
													"address":  "${addresses[0].description.replace(/\n/g, ' ')}",
												`}
												${phones.length > 0 && `
													"contactPoint": {
														"@type": "ContactPoint",
														"telephone": "${phones[0].phoneNumber}",
														"contactType": "customer service"
													},
													"telephone": "${phones[0].phoneNumber}",
												`}
												${generalData && generalData.logo ? `
													"logo": "${process.env.REACT_APP_API_ENDPOINT}${generalData.logo.path}"
												` : ''}
											}
										},
										{
											"@type": "BreadcrumbList",
											"itemListElement": [
												{
													"@type": "ListItem",
													"name": "Inocrowd",
													"position": 1,
													"item": "${process.env.REACT_APP_WEBSITE_ENDPOINT}"
												},
												{
													"@type": "ListItem",
													"name": "${formatMessage({ id: 'navigation.faq' })}",
													"position": 2,
													"item": "${process.env.REACT_APP_WEBSITE_ENDPOINT}/how-it-works/faq"
												}
											]
										}
									]
								}`}
				/>
				<Fragment>
					{faq.title
							&& (
								<div className="container container-fluid">
									<div className="row">
										<div className="col-xs-12 center-xs middle-xs">
											<h1 className="title">{faq.title}</h1>
										</div>
									</div>
								</div>
							)
					}
					<section className="faq">
						<div className="container container-fluid">
							{faq.groups && faq.groups.map((group, index) => (
								<div className="row group" key={index}>
									{group.value.title
										&& <span className="group-name">{group.value.title}</span>
									}
									{group.value.group.map((item, i) => (
										<div className="col-xs-12 start-xs top-xs item-col" key={i}>
											<FaqItem data={item} index={(`${index}-${i}`)} handleClick={this.handleClick} isOpen={openItem === (`${index}-${i}`)} />
										</div>
									))}
								</div>
							))}
						</div>
					</section>
				</Fragment>
			</div>
		);
	}
}

const mapToProps = ({ language, faq, addresses, phones, generalData }) => ({
	language,
	faq,
	addresses,
	phones,
	generalData,
});

export default injectIntl(
	connect(
		mapToProps,
		actions,
	)(FaqPage),
);

export { FaqPage };
